import React, { useState } from 'react';
import { Shield, Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

const TradingRulesList = ({ rules, onAddRule, onEditRule, onToggleRule, onDeleteRule }) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const oobRules = rules.filter((r) => r.category === 'oob');
  const customRules = rules.filter((r) => r.category === 'custom' && r.is_active);

  const handleDelete = (rule) => {
    if (deleteConfirm === rule.id) {
      onDeleteRule(rule.id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(rule.id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  return (
    <div className="space-y-4">
      {/* OOB Rules */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-foreground">Standard Rules</h4>
          <span className="text-xs text-muted-foreground">({oobRules.filter(r => r.is_active).length} active)</span>
        </div>
        <div className="space-y-1">
          {oobRules.map((rule) => (
            <div
              key={rule.id}
              className={`flex items-center justify-between px-3 py-2 rounded-md border border-border ${
                rule.is_active ? 'bg-card' : 'bg-muted/30 opacity-60'
              }`}
            >
              <div className="flex-1 min-w-0">
                <span className={`text-sm ${rule.is_active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                  {rule.label}
                </span>
                {rule.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{rule.description}</p>
                )}
              </div>
              <button
                onClick={() => onToggleRule(rule.id, !rule.is_active)}
                className="ml-3 flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                title={rule.is_active ? 'Deactivate rule' : 'Activate rule'}
              >
                {rule.is_active ? (
                  <ToggleRight className="w-5 h-5 text-green-500" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Rules */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-foreground">Custom Rules</h4>
            <span className="text-xs text-muted-foreground">({customRules.length})</span>
          </div>
          <button
            onClick={onAddRule}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Rule
          </button>
        </div>

        {customRules.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-border rounded-md">
            <p className="text-sm text-muted-foreground">No custom rules yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Add rules specific to your trading plan.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {customRules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center justify-between px-3 py-2 rounded-md border border-border bg-card group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">{rule.label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                      Custom
                    </span>
                  </div>
                  {rule.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{rule.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onEditRule(rule)}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                    title="Edit rule"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(rule)}
                    className={`p-1 transition-colors ${
                      deleteConfirm === rule.id
                        ? 'text-red-500'
                        : 'text-muted-foreground hover:text-red-500'
                    }`}
                    title={deleteConfirm === rule.id ? 'Click again to confirm' : 'Delete rule'}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingRulesList;
